import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Radio, BookOpen, Sparkles, Gamepad2, Check, X, CalendarDays, Users,
  ArrowRight, ArrowLeft, Loader2, Star, ChevronDown, Clock, MapPin, PlayCircle, Mic,
  Flame, GraduationCap, Trophy, Smartphone,
} from "lucide-react";
import { LivePassSignupCard } from "@/components/LivePassSignupCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WP = "https://wespeakspanish-cr.com/wp-content/uploads/2026/10";
/* Video vertical de la escuela. Cuando el dueño lo grabe, se pega la URL acá y
   la sección aparece sola; mientras esté vacío no se muestra nada. */
const GO_VIDEO = "";
const GO_VIDEO_POSTER = "";

const DIAS_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DIAS_ES = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MESES_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "set", "oct", "nov", "dic"];

const fecha = (iso, en) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return { dia: (en ? DIAS_EN : DIAS_ES)[dt.getDay()], num: d, mes: (en ? MESES_EN : MESES_ES)[m - 1] };
};

const hora = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

/* Los textos siguen el documento oficial de la escuela: «WSS GO: What It Is,
   What It Isn’t, and Why We Created It». */
const T = {
  en: {
    nav_why: "Why", nav_classes: "Classes", nav_inside: "What’s inside",
    nav_price: "Price", nav_faq: "FAQ",
    cta: (d) => `${d} days free`,
    kicker: "Spanish learning that goes with you",
    h1a: "Your Spanish doesn’t have to", h1b: "fade away again",
    sub: "WSS GO is a flexible learning space that helps you maintain, practice and keep developing the Spanish you already worked hard for — live classes, resources, verb practice and games, all in one place.",
    b1: "Live sessions with our teachers, in Costa Rica time",
    b2: "Practice on your own: resources, conjugation, videos and games",
    b3: "No fixed weekly schedule and no contract",
    why_kicker: "Why we created it", why_h: "Life gets in the way. Your Spanish shouldn’t pay for it.",
    why_p1: "Over the years we’ve seen students make wonderful progress, gain confidence and really start communicating. We’ve also seen what happens next: work schedules change, people travel, family takes over. Classes pause — and little by little, the Spanish they worked so hard for starts to fade.",
    why_p2: "That’s one of the main reasons we created WSS GO. Learning Spanish should be a continuous experience, not something that only happens during a scheduled class.",
    pillars: [["WSS Live", "Classes with real teachers"], ["Practice & resources", "Tools to keep learning"],
              ["Flexible schedule", "Learn at your own pace"], ["From anywhere", "Computer, tablet or phone"]],
    week_kicker: "Upcoming", week_h: "Classes you could join",
    week_sub: "Real calendar, Costa Rica time (GMT-6). It updates automatically.",
    week_empty: "We’re putting together the calendar for the coming weeks. Sign up and we’ll write to you as soon as the classes are published.",
    inside_kicker: "What’s inside", inside_h: "Different ways to keep Spanish in your week",
    f1_t: "WSS Live sessions", f1_d: "Short, interactive online classes led by our Spanish teachers. You see the week’s calendar and book the one that fits you; if you can’t make it, you cancel and take another.",
    f2_t: "Student resources", f2_d: "Grammar explanations, vocabulary, educational material and practical exercises. Open what you need, whenever you need it, as many times as you want.",
    f3_t: "Conjugation practice", f3_d: "Activities to strengthen verb conjugation and sentence building — the part everybody struggles with, broken into short sessions you can do from your phone.",
    f4_t: "WSS Game Zone", f4_d: "Interactive games that make practicing Spanish engaging. Vocabulary and grammar by topic and level, in ten minutes that don’t feel like homework.",
    more_h: "And also", growing: "Growing",
    more: [
      ["Spanish learning videos", "Video lessons to review and reinforce key concepts.", false],
      ["Speaking practice", "Opportunities to build fluency and confidence when you speak.", false],
      ["Duolingo practice", "Extra activities that complement your independent learning.", false],
      ["On-demand courses", "A developing collection of structured lessons at your own pace.", true],
      ["Daily practices & challenges", "Short activities to keep you in contact with Spanish — this part keeps expanding.", true],
    ],
    diff_h: "We’re not recreating a classroom on a screen",
    diff_p: "Someone takes a 30-minute live session on Monday, reviews verbs on Tuesday, watches a short video on Wednesday and practices vocabulary for a few minutes on Thursday. Someone else only opens the platform on weekends. Both make progress. The goal isn’t studying for hours every day — it’s making Spanish a regular part of everyday life.",
    how_kicker: "How it works", how_h: "Inside in three steps",
    s1_t: "Sign up here", s1_d: "Name, email and WhatsApp. Thirty seconds, no card.",
    s2_t: "Get your code", s2_d: "A 6-digit code lands in your inbox right away.",
    s3_t: "Book your first class", s3_d: "You’re in. Pick a class from the calendar and join.",
    price_kicker: "Price, with no fine print", price_h: (d) => `${d} days free, then $`,
    price_after: "a month", price_note: "No card required to start. Nothing is charged automatically.",
    price_day8: (d) => `What happens on day ${d + 1}: we email you the invoice with a payment link. If you decide not to continue, you do nothing — we stop invoicing you and that’s it.`,
    price_badge: (d) => `First ${d} days free`,
    video_kicker: "30 seconds", video_h: "Why we built WSS GO",
    back_site: "School site",
    video_p: "Our team explains, in half a minute, what WSS GO is for and what it isn’t. No fine print.",
    price_plan: "WSS GO monthly pass",
    price_incl_h: "Everything is included",
    price_incl: ["Live sessions on the WSS Live calendar",
                 "Resources, conjugation and WSS Game Zone",
                 "New content added every month",
                 "No enrollment, no fixed schedule, no contract",
                 "Pause whenever you want — no debt piles up"],
    price_cancel: "Cancel any month. The pass simply stops.",
    fit_h: "Who WSS GO is designed for",
    fit: ["Former students who want to refresh what they learned",
          "Expats who need Spanish for everyday life in Costa Rica",
          "Professionals and travelers with demanding schedules",
          "Independent learners who want to feel more confident speaking",
          "Current students, as a complement to their regular classes"],
    fit_note: "It’s especially valuable if you already know some Spanish.",
    nofit_h: "What WSS GO isn’t",
    nofit: [
      ["A complete course for absolute beginners", "Beginners find useful resources, but there isn’t yet a full step-by-step program from zero. Building those structured courses is part of our vision."],
      ["A replacement for regular classes", "Regular classes give structure, continuity and personalized feedback. WSS GO gives independence, flexibility and extra practice. They complement each other."],
      ["Just a pile of videos and downloads", "It mixes live teaching, independent study, practice activities and interaction. We want you using the language, not only consuming content."],
      ["A fixed weekly schedule", "You choose when to open resources, watch a video or practice. Live sessions happen at set times, and you pick the ones that fit you."],
      ["A promise of fluency without effort", "Languages need consistency and participation. WSS GO gives you the tools and the opportunities; your part is essential."],
    ],
    school_kicker: "Part of We Speak Spanish CR",
    school_h: "Not a competing approach — another way to support the same journey",
    school_p: "Our regular programs keep offering structured learning in group and private classes, online and in person in Santa Ana, Atenas and Grecia. WSS GO expands what we can offer beyond those classes: practice for current students, a way back for former students, and an accessible door into our community. When you need more structure, our regular programs are right there.",
    faq_h: "Questions people ask us",
    faq: [
      ["Do I have to show up at a fixed time?", "No. You see the week’s calendar and book the sessions that fit you. If something comes up, you cancel and take another one."],
      ["What level do I need?", "WSS GO is especially valuable if you already know some Spanish. Sessions are grouped by level, and if you’re not sure which one fits, we’ll help you find it after your first class."],
      ["Is this a full Spanish course from zero?", "Not yet. If you’re starting from absolute zero, our regular courses fit you better — and you can combine them with WSS GO later."],
      ["What happens when the free days end?", "We email you the invoice with a payment link. Nothing is charged automatically, so if you don’t want to continue, you simply do nothing."],
      ["Who teaches the live sessions?", "Costa Rican teachers from our school — the same team that teaches our regular courses."],
    ],
    final_h: "Learn it. Improve it. Keep it alive.",
    final_sub: "Spanish learning that goes with you.",
    foot: "We Speak Spanish CR · Santa Ana, Costa Rica",
  },
  es: {
    nav_why: "Por qué", nav_classes: "Clases", nav_inside: "Qué incluye",
    nav_price: "Precio", nav_faq: "Preguntas",
    cta: (d) => `${d} días gratis`,
    kicker: "El español que te acompaña",
    h1a: "Tu español no tiene por qué", h1b: "volver a apagarse",
    sub: "WSS GO es un espacio flexible para mantener, practicar y seguir desarrollando el español que ya te costó aprender — clases en vivo, recursos, práctica de verbos y juegos, todo en un solo lugar.",
    b1: "Clases en vivo con nuestros profes, en hora de Costa Rica",
    b2: "Práctica por tu cuenta: recursos, conjugación, videos y juegos",
    b3: "Sin horario fijo cada semana y sin contrato",
    why_kicker: "Por qué lo creamos", why_h: "La vida se atraviesa. Tu español no tiene por qué pagarlo.",
    why_p1: "En estos años hemos visto estudiantes avanzar muchísimo, agarrar confianza y empezar a comunicarse de verdad. También hemos visto lo que pasa después: cambia el trabajo, viajan, la familia ocupa todo. Las clases se pausan — y poco a poco, el español que tanto les costó se empieza a apagar.",
    why_p2: "Esa es una de las razones principales por las que creamos WSS GO. Aprender español debería ser algo continuo, no algo que solo pasa dentro de una clase con horario.",
    pillars: [["WSS Live", "Clases con profes de verdad"], ["Práctica y recursos", "Herramientas para seguir"],
              ["Horario flexible", "Aprendés a tu ritmo"], ["Desde donde sea", "Compu, tablet o teléfono"]],
    week_kicker: "Próximas", week_h: "Clases a las que podrías entrar",
    week_sub: "Calendario real, hora de Costa Rica (GMT-6). Se actualiza solo.",
    week_empty: "Estamos armando el calendario de las próximas semanas. Apuntate y te escribimos apenas se publiquen las clases.",
    inside_kicker: "Qué incluye", inside_h: "Varias formas de meter el español en tu semana",
    f1_t: "Sesiones WSS Live", f1_d: "Clases cortas e interactivas en línea, con nuestros profes. Ves el calendario de la semana y reservás la que te sirve; si no podés, la cancelás y tomás otra.",
    f2_t: "Recursos del estudiante", f2_d: "Explicaciones de gramática, vocabulario, material educativo y ejercicios prácticos. Abrís lo que necesitás, cuando lo necesitás, las veces que querás.",
    f3_t: "Práctica de conjugación", f3_d: "Actividades para afirmar la conjugación de verbos y la construcción de oraciones — lo que a todos se les atraviesa, partido en prácticas cortas que hacés desde el teléfono.",
    f4_t: "WSS Game Zone", f4_d: "Juegos interactivos que hacen entretenido practicar. Vocabulario y gramática por tema y nivel, en diez minutos que no se sienten tarea.",
    more_h: "Y además", growing: "Creciendo",
    more: [
      ["Videos para aprender español", "Lecciones en video para repasar y afirmar lo importante.", false],
      ["Práctica de conversación", "Espacios para soltar la fluidez y la confianza al hablar.", false],
      ["Práctica con Duolingo", "Actividades extra que acompañan tu estudio por cuenta propia.", false],
      ["Cursos a tu ritmo", "Una colección en desarrollo de lecciones ordenadas, a tu paso.", true],
      ["Prácticas y retos diarios", "Actividades cortas para no perder el contacto con el español — esta parte sigue creciendo.", true],
    ],
    diff_h: "No estamos copiando un aula dentro de una pantalla",
    diff_p: "Alguien toma una sesión en vivo de 30 minutos el lunes, repasa verbos el martes, ve un video corto el miércoles y practica vocabulario unos minutos el jueves. Otra persona abre la plataforma solo los fines de semana. Las dos avanzan. La meta no es estudiar horas todos los días: es que el español vuelva a ser parte de tu día a día.",
    how_kicker: "Cómo funciona", how_h: "Adentro en tres pasos",
    s1_t: "Te apuntás acá", s1_d: "Nombre, correo y WhatsApp. Treinta segundos, sin tarjeta.",
    s2_t: "Te llega tu código", s2_d: "Un código de 6 dígitos llega a tu correo de una.",
    s3_t: "Reservás tu primera clase", s3_d: "Ya estás adentro. Elegís una clase del calendario y entrás.",
    price_kicker: "El precio, sin letra chica", price_h: (d) => `${d} días gratis, después $`,
    price_after: "al mes", price_note: "No se pide tarjeta para empezar. No se cobra nada automáticamente.",
    price_day8: (d) => `Qué pasa el día ${d + 1}: te mandamos la factura por correo con su enlace de pago. Si decidís no seguir, no hacés nada — dejamos de facturarte y listo.`,
    price_badge: (d) => `Primeros ${d} días gratis`,
    video_kicker: "30 segundos", video_h: "Por qué hicimos WSS GO",
    back_site: "Sitio de la escuela",
    video_p: "En medio minuto te contamos para qué sirve WSS GO y para qué no. Sin letra chica.",
    price_plan: "Pase mensual WSS GO",
    price_incl_h: "Todo va incluido",
    price_incl: ["Sesiones en vivo del calendario de WSS Live",
                 "Recursos, conjugación y WSS Game Zone",
                 "Contenido nuevo cada mes",
                 "Sin matrícula, sin horario fijo, sin contrato",
                 "Lo pausás cuando querés — no se acumula deuda"],
    price_cancel: "Lo cancelás cuando querás. El pase simplemente se detiene.",
    fit_h: "Para quién está pensado WSS GO",
    fit: ["Exalumnos que quieren refrescar lo que aprendieron",
          "Expats que necesitan el español del día a día en Costa Rica",
          "Profesionales y viajeros con agendas apretadas",
          "Quien estudia por su cuenta y quiere soltarse al hablar",
          "Estudiantes actuales, como complemento de sus clases regulares"],
    fit_note: "Es especialmente valioso si ya sabés algo de español.",
    nofit_h: "Lo que WSS GO no es",
    nofit: [
      ["Un curso completo para quien empieza de cero", "Quien empieza encuentra recursos útiles, pero todavía no hay un programa completo paso a paso desde cero. Construir esos cursos ordenados es parte de nuestra visión."],
      ["Un reemplazo de las clases regulares", "Las clases regulares dan estructura, continuidad y acompañamiento personalizado. WSS GO da independencia, flexibilidad y práctica extra. Se complementan."],
      ["Un montón de videos y archivos para bajar", "Mezcla clases en vivo, estudio por cuenta propia, actividades de práctica e interacción. Queremos que usés el idioma, no que solo consumás contenido."],
      ["Un horario fijo cada semana", "Vos elegís cuándo abrir los recursos, ver un video o practicar. Las sesiones en vivo tienen su hora, y vos escogés las que te sirven."],
      ["Una promesa de fluidez sin esfuerzo", "Un idioma pide constancia y participación. WSS GO pone las herramientas y las oportunidades; tu parte es esencial."],
    ],
    school_kicker: "Parte de We Speak Spanish CR",
    school_h: "No compite con las clases: es otra forma de acompañar el mismo camino",
    school_p: "Nuestros programas regulares siguen dando el aprendizaje estructurado, en grupo y privado, en línea y presencial en Santa Ana, Atenas y Grecia. WSS GO amplía lo que podemos ofrecer más allá de esas clases: práctica para quien estudia hoy, una forma de volver para quien se fue, y una puerta accesible a nuestra comunidad. Cuando necesités más estructura, los programas regulares están ahí.",
    faq_h: "Lo que más nos preguntan",
    faq: [
      ["¿Tengo que entrar a una hora fija?", "No. Ves el calendario de la semana y reservás las sesiones que te sirven. Si te sale algo, la cancelás y tomás otra."],
      ["¿Qué nivel necesito?", "WSS GO es especialmente valioso si ya sabés algo de español. Las sesiones van por nivel y, si no estás seguro, te ubicamos después de tu primera clase."],
      ["¿Es un curso completo desde cero?", "Todavía no. Si empezás de cero, te sirven más nuestros cursos regulares — y más adelante los podés combinar con WSS GO."],
      ["¿Qué pasa cuando se acaban los días gratis?", "Te mandamos la factura por correo con su enlace de pago. No se cobra nada automático, así que si no querés seguir, simplemente no hacés nada."],
      ["¿Quién da las sesiones en vivo?", "Profes ticos de nuestra escuela — el mismo equipo de los cursos regulares."],
    ],
    final_h: "Aprendelo. Mejoralo. Mantenelo vivo.",
    final_sub: "El español que te acompaña.",
    foot: "We Speak Spanish CR · Santa Ana, Costa Rica",
  },
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visto, setVisto] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setVisto(true),
                                        { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
         style={{
           opacity: visto ? 1 : 0,
           transform: visto ? "none" : "translateY(22px)",
           transition: `opacity .7s ease ${delay}ms, transform .7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
         }}>
      {children}
    </div>
  );
};

export default function GoLanding() {
  const lang = "en";
  const [data, setData] = useState(null);
  const [abierta, setAbierta] = useState(0);
  const t = T[lang];
  const en = lang === "en";

  useEffect(() => {
    axios.get(`${API}/public/live/sessions`, { params: { limit: 6 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ sessions: [], price: 29.99, trial_days: 7 }));
    // Contador propio de visitas: una sola vez por pestaña, sin cookies.
    if (!sessionStorage.getItem("go_visit")) {
      sessionStorage.setItem("go_visit", "1");
      axios.post(`${API}/public/visit`, { page: "go", ref: document.referrer || "" })
        .catch(() => {});
    }
  }, []);

  const dias = data?.trial_days ?? 7;
  const precio = Number(data?.price ?? 29.99);
  const query = new URLSearchParams(window.location.search);
  const paso = { code: query.get("step") === "code", email: query.get("email") || "" };

  const features = [
    { id: "wss-live", icon: Radio, t: t.f1_t, d: t.f1_d, img: `${WP}/foto1.2.png`, tono: "text-orange-600 bg-orange-50" },
    { id: "resources", icon: BookOpen, t: t.f2_t, d: t.f2_d, img: `${WP}/foto1.1.png`, tono: "text-sky-600 bg-sky-50" },
    { id: "conjugation", icon: Sparkles, t: t.f3_t, d: t.f3_d, img: `${WP}/foto1.4.png`, tono: "text-violet-600 bg-violet-50" },
    { id: "game-zone", icon: Gamepad2, t: t.f4_t, d: t.f4_d, img: `${WP}/foto1.5.png`, tono: "text-rose-600 bg-rose-50" },
  ];
  const masIconos = [PlayCircle, Mic, Trophy, GraduationCap, Flame];

  const Cta = ({ testid, className = "" }) => (
    <a href="#signup" data-testid={testid}
       className={`inline-flex items-center gap-2 bg-[#ff731d] hover:bg-[#e05d0c] text-white font-bold rounded-xl px-7 py-3.5 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${className}`}>
      {t.cta(dias)} <ArrowRight className="h-4 w-4" />
    </a>
  );

  return (
    <div className="min-h-screen bg-white text-[#0f172a]" data-testid="go-landing">
      {/* ---------- barra fija ---------- */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0d1b2a]/95 border-b border-white/10"
              data-testid="go-header">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <a href="#top" className="flex items-center text-white" data-testid="header-logo">
            <img src="/wssgo-logo-white.png" alt="WSS GO — by We Speak Spanish"
                 className="h-10 sm:h-11 w-auto transition-transform duration-200 hover:scale-[1.03]"
                 data-testid="header-logo-img" />
          </a>
          <nav className="hidden md:flex items-center gap-7 text-[13.5px] text-slate-300">
            <a href="#why" className="hover:text-white transition-colors" data-testid="nav-why">{t.nav_why}</a>
            <a href="#classes" className="hover:text-white transition-colors" data-testid="nav-classes">{t.nav_classes}</a>
            <a href="#inside" className="hover:text-white transition-colors" data-testid="nav-inside">{t.nav_inside}</a>
            <a href="#price" className="hover:text-white transition-colors" data-testid="nav-price">{t.nav_price}</a>
            <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq">{t.nav_faq}</a>
            <a href="https://wespeakspanish-cr.com" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors border-l border-white/15 pl-7"
               data-testid="nav-back-site">
              <ArrowLeft className="h-3.5 w-3.5" /> {t.back_site}
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="#signup" data-testid="header-cta-btn"
               className="hidden sm:inline-flex items-center gap-1.5 bg-[#ff731d] hover:bg-[#e05d0c] text-white text-[13.5px] font-bold rounded-lg px-4 py-2 transition-colors">
              {t.cta(dias)}
            </a>
          </div>
        </div>
      </header>

      {/* ---------- portada ---------- */}
      <section id="top" className="relative bg-[#0d1b2a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
             style={{ backgroundImage: "radial-gradient(circle at 15% 15%, #fff 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
        <div className="absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-[#ff731d]/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-[0.16em] text-[#ff9d5c]">
                <MapPin className="h-3.5 w-3.5" /> {t.kicker}
              </span>
              <h1 className="font-go text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mt-4">
                {t.h1a}{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">{t.h1b}</span>
                  <span className="absolute left-0 right-0 bottom-0.5 sm:bottom-1.5 h-2.5 sm:h-3 bg-[#ff731d]/40 -rotate-1" />
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 max-w-xl leading-relaxed">{t.sub}</p>
            </Reveal>
            <Reveal delay={120}>
              <ul className="mt-8 space-y-3 max-w-xl" data-testid="hero-bullets">
                {[t.b1, t.b2, t.b3].map((b) => (
                  <li key={b} className="flex gap-3 text-[15px] text-slate-200">
                    <span className="h-5 w-5 rounded-full bg-emerald-500/15 grid place-items-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-emerald-400" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-5 mt-9">
                <Cta testid="hero-cta-btn" />
                <span className="text-[13px] text-slate-400">{t.price_note}</span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={200} className="lg:col-span-5 w-full">
            <div id="signup" className="bg-white text-slate-900 rounded-2xl p-6 shadow-2xl shadow-black/30 scroll-mt-24"
                 data-testid="hero-signup-form">
              <LivePassSignupCard trialDays={dias} lang={lang} source="wss_go_landing" accent
                                  initialEmail={paso.email} initialDone={paso.code} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- video de la escuela (aparece cuando hay URL) ---------- */}
      {GO_VIDEO && (
        <section id="video" className="bg-[#0d1b2a] text-white border-t border-white/5 scroll-mt-20">
          <div className="max-w-5xl mx-auto px-5 py-14 lg:py-20 grid lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-10 lg:gap-14 items-center">
            <Reveal>
              <div className="relative mx-auto w-full max-w-[300px] aspect-[9/16] rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl shadow-black/50 bg-black">
                <video className="h-full w-full object-cover" controls playsInline preload="metadata"
                       poster={GO_VIDEO_POSTER || undefined} data-testid="go-video">
                  <source src={GO_VIDEO} />
                </video>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <span className="text-[15px] font-bold uppercase tracking-[0.16em] text-[#ff9d5c]">{t.video_kicker}</span>
              <h2 className="font-go text-3xl sm:text-4xl font-bold mt-3 leading-tight">{t.video_h}</h2>
              <p className="text-[15.5px] text-slate-300 leading-relaxed mt-5 max-w-lg">{t.video_p}</p>
              <div className="mt-8"><Cta testid="video-cta-btn" /></div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- por qué lo creamos ---------- */}
      <section id="why" className="bg-[#ffeede] border-b border-orange-200/70 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <span className="text-[15px] font-bold uppercase tracking-[0.16em] text-[#c2410c]">{t.why_kicker}</span>
            <h2 className="font-go text-3xl sm:text-4xl font-bold mt-3 max-w-xl leading-tight">{t.why_h}</h2>
            <p className="text-[15.5px] text-slate-600 leading-relaxed mt-5 max-w-xl">{t.why_p1}</p>
            <p className="text-[15.5px] text-slate-900 leading-relaxed mt-4 max-w-xl border-l-[3px] border-[#ff731d] pl-4 font-medium">
              {t.why_p2}
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-8 max-w-xl" data-testid="why-pillars">
              {t.pillars.map(([titulo, texto], i) => {
                const Icono = [Radio, BookOpen, CalendarDays, Smartphone][i];
                return (
                  <div key={titulo} className="flex items-start gap-3 bg-white/70 border border-orange-100 rounded-xl px-4 py-3"
                       data-testid={`pillar-${i + 1}`}>
                    <span className="h-8 w-8 rounded-lg bg-[#ff731d]/10 grid place-items-center shrink-0">
                      <Icono className="h-4 w-4 text-[#ff731d]" />
                    </span>
                    <span>
                      <span className="block font-go font-bold text-[14.5px] leading-tight">{titulo}</span>
                      <span className="block text-[12.5px] text-slate-500 mt-0.5">{texto}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="max-w-[560px] mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-orange-900/20 hover:-translate-y-1 transition-transform duration-500">
              <img src="/wss-go-poster.png" alt="WSS GO — Spanish learning that goes with you" loading="lazy"
                   className="w-full h-auto object-cover block" data-testid="why-poster" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- próximas clases ---------- */}
      <section id="classes" className="bg-[#eaf1fa] border-y border-[#d6e3f3] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-[0.16em] text-[#c2410c]">
              <CalendarDays className="h-3.5 w-3.5" /> {t.week_kicker}
            </span>
            <h2 className="font-go text-3xl sm:text-4xl font-bold mt-3">{t.week_h}</h2>
            <p className="text-slate-500 mt-2 text-[15px]">{t.week_sub}</p>
          </Reveal>

          {!data ? (
            <div className="py-14 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-300" /></div>
          ) : data.sessions?.length ? (
            <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="live-calendar-card">
              {data.sessions.map((s, i) => {
                const f = fecha(s.date, en);
                return (
                  <Reveal key={i} delay={i * 70}>
                    <div className="group h-full bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#ff731d] hover:shadow-xl hover:shadow-orange-500/5 hover:-translate-y-0.5 transition-all duration-300"
                         data-testid={`session-card-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[11px] font-bold uppercase tracking-wide text-[#ff731d]">
                            {f.dia} {f.num} {f.mes}
                          </div>
                          <div className="font-go font-bold text-[17px] mt-1.5 leading-snug">{s.title}</div>
                        </div>
                        {(s.levels || []).length > 0 && (
                          <span className="text-[10px] font-bold text-slate-600 bg-slate-100 rounded px-1.5 py-1 shrink-0">
                            {s.levels.join(" · ")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-[13px] text-slate-500 mt-4 pt-3.5 border-t border-slate-100">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {hora(s.start_time)}{s.end_time ? ` – ${hora(s.end_time)}` : ""}
                        </span>
                        {s.capacity > 0 && (
                          <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {s.capacity}</span>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          ) : (
            <p className="mt-9 text-[15px] text-slate-500 border border-dashed border-slate-300 rounded-2xl p-10 text-center bg-white"
               data-testid="live-calendar-empty">
              {t.week_empty}
            </p>
          )}
        </div>
      </section>

      {/* ---------- qué incluye ---------- */}
      <section id="inside" className="bg-[#fff8f0] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <span className="text-[15px] font-bold uppercase tracking-[0.16em] text-[#c2410c]">{t.inside_kicker}</span>
            <h2 className="font-go text-3xl sm:text-4xl font-bold mt-3 max-w-2xl">{t.inside_h}</h2>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <Reveal key={f.id} delay={i * 80}>
                <div className="group h-full flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-xl hover:shadow-slate-900/5 hover:-translate-y-1 transition-all duration-300"
                     data-testid={`feature-${f.id}`}>
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={f.img} alt={f.t} loading="lazy"
                         className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                         onError={(e) => { e.currentTarget.style.opacity = 0; }} />
                    <span className={`absolute left-4 bottom-4 inline-flex h-10 w-10 rounded-xl items-center justify-center shadow-lg shadow-black/10 ${f.tono}`}>
                      <f.icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="font-go text-xl sm:text-2xl font-bold">{f.t}</h3>
                    <p className="text-[15px] text-slate-600 leading-relaxed mt-2.5 flex-1">{f.d}</p>
                    <a href="#signup" className="inline-flex items-center gap-1.5 text-[14px] font-bold text-[#ff731d] hover:gap-2.5 transition-all mt-5"
                       data-testid={`feature-cta-${f.id}`}>
                      {t.cta(dias)} <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* y además */}
          <Reveal delay={60}>
            <div className="mt-16 lg:mt-20 border-t border-slate-200 pt-12" data-testid="more-inside">
              <h3 className="font-go text-xl font-bold text-slate-500">{t.more_h}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {t.more.map(([titulo, texto, creciendo], i) => {
                  const Icono = masIconos[i] || Star;
                  return (
                    <div key={titulo} data-testid={`more-item-${i + 1}`}
                         className="bg-white border border-orange-200/80 rounded-xl p-5 shadow-sm shadow-orange-900/5 hover:border-[#ff731d] hover:shadow-md transition-all duration-300">
                      <div className="flex items-start justify-between gap-2">
                        <Icono className="h-4 w-4 text-[#ff731d] mt-0.5" />
                        {creciendo && (
                          <span className="text-[9.5px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                            {t.growing}
                          </span>
                        )}
                      </div>
                      <h4 className="font-go font-bold text-[15px] mt-3">{titulo}</h4>
                      <p className="text-[13.5px] text-slate-600 mt-1.5 leading-relaxed">{texto}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- fotos + en qué es diferente ---------- */}
      <section className="bg-[#eaf1fa] border-y border-[#d6e3f3]">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="student-photos">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="aspect-square rounded-xl overflow-hidden bg-slate-200">
                  <img src={`${WP}/foto${n}.png`} alt="" loading="lazy"
                       className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                       onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="max-w-3xl mt-14 lg:mt-20" data-testid="difference">
              <h2 className="font-go text-2xl sm:text-3xl font-bold leading-snug">{t.diff_h}</h2>
              <p className="text-[15.5px] text-slate-600 leading-relaxed mt-4">{t.diff_p}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- cómo funciona ---------- */}
      <section className="bg-[#fff8f0]">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <span className="text-[15px] font-bold uppercase tracking-[0.16em] text-[#c2410c]">{t.how_kicker}</span>
            <h2 className="font-go text-3xl sm:text-4xl font-bold mt-3">{t.how_h}</h2>
            <div className="grid md:grid-cols-3 gap-5 mt-10" data-testid="how-it-works">
              {[[t.s1_t, t.s1_d], [t.s2_t, t.s2_d], [t.s3_t, t.s3_d]].map(([titulo, texto], i) => (
                <div key={titulo} className="relative bg-white border border-orange-200/80 rounded-2xl p-6 shadow-sm shadow-orange-900/5 hover:border-[#ff731d] hover:shadow-lg hover:shadow-orange-500/10 hover:-translate-y-0.5 transition-all duration-300"
                     data-testid={`step-${i + 1}`}>
                  <span className="font-go text-5xl font-extrabold text-[#ff731d]/15 absolute top-3 right-5 select-none">{i + 1}</span>
                  <h3 className="font-go font-bold text-lg relative">{titulo}</h3>
                  <p className="text-[14.5px] text-slate-600 mt-2 relative">{texto}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- precio ---------- */}
      <section id="price" className="relative bg-[#0d1b2a] text-white scroll-mt-20 overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-[42rem] rounded-full bg-[#ff731d]/20 blur-[90px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-[15px] font-bold uppercase tracking-[0.16em] text-[#ff9d5c]">{t.price_kicker}</span>
              <p className="text-slate-300 mt-4 text-[15.5px] leading-relaxed">{t.price_day8(dias)}</p>
            </div>

            <div className="mt-10 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] bg-white text-[#0f172a] rounded-3xl overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/10"
                 data-testid="pricing-card">
              {/* lado del precio */}
              <div className="relative p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-slate-200/80 flex flex-col justify-center">
                <span className="self-start inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-3 py-1 text-[11.5px] font-bold uppercase tracking-wider"
                      data-testid="pricing-trial-badge">
                  <Sparkles className="h-3.5 w-3.5" /> {t.price_badge(dias)}
                </span>
                <p className="mt-5 text-[13px] font-bold uppercase tracking-[0.18em] text-[#c2410c]">{t.price_plan}</p>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="font-go text-2xl font-extrabold text-slate-400 self-start mt-2">$</span>
                  <span className="font-go text-6xl sm:text-7xl font-extrabold tracking-tight text-[#0d1b2a] leading-none"
                        data-testid="pricing-amount">{precio.toFixed(2)}</span>
                  <span className="text-base font-bold text-slate-500">/ {t.price_after.replace(/^(a|al) /, "")}</span>
                </div>
                <div className="mt-7">
                  <Cta testid="pricing-cta-btn" className="w-full justify-center" />
                  <p className="text-[12.5px] text-slate-500 mt-3 text-center" data-testid="pricing-note">{t.price_note}</p>
                </div>
              </div>

              {/* lado de lo que incluye */}
              <div className="bg-[#fffaf5] p-8 lg:p-10">
                <h3 className="font-go text-lg font-extrabold text-[#0d1b2a]">{t.price_incl_h}</h3>
                <ul className="mt-5 space-y-3.5" data-testid="pricing-includes">
                  {t.price_incl.map((it) => (
                    <li key={it} className="flex gap-3 text-[15px] text-slate-700 leading-snug">
                      <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#ff731d]/12 text-[#ff731d] grid place-items-center">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                      {it}
                    </li>
                  ))}
                </ul>
                <p className="mt-7 pt-5 border-t border-orange-200/70 text-[13.5px] text-slate-500 italic">{t.price_cancel}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- para quién es ---------- */}
      <section className="bg-[#fff8f0]">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <div className="bg-emerald-100/70 border border-emerald-300 rounded-2xl p-7 lg:p-9" data-testid="who-is-for">
              <h2 className="font-go text-2xl sm:text-3xl font-bold text-emerald-950">{t.fit_h}</h2>
              <p className="text-[14.5px] text-emerald-800 mt-2">{t.fit_note}</p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {t.fit.map((f) => (
                  <li key={f} className="flex gap-3 text-[15px] text-slate-700">
                    <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-1" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* lo que no es */}
          <Reveal delay={100}>
            <div className="mt-14" data-testid="who-is-not-for">
              <h2 className="font-go text-2xl sm:text-3xl font-bold">{t.nofit_h}</h2>
              <div className="mt-7 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {t.nofit.map(([titulo, texto], i) => (
                  <div key={titulo} data-testid={`not-item-${i + 1}`}
                       className="bg-white border border-slate-200/90 rounded-xl p-5 shadow-sm shadow-slate-900/5">
                    <div className="flex items-start gap-2.5">
                      <X className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                      <h3 className="font-go font-bold text-[15.5px] leading-snug">{titulo}</h3>
                    </div>
                    <p className="text-[13.5px] text-slate-600 mt-2.5 leading-relaxed">{texto}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- la escuela ---------- */}
      <section className="bg-[#eaf1fa] border-y border-[#d6e3f3]">
        <div className="max-w-3xl mx-auto px-5 py-16 lg:py-20 text-center" data-testid="school-connection">
          <Reveal>
            <span className="text-[15px] font-bold uppercase tracking-[0.16em] text-[#c2410c]">{t.school_kicker}</span>
            <h2 className="font-go text-2xl sm:text-3xl font-bold mt-3 leading-snug">{t.school_h}</h2>
            <p className="text-[15.5px] text-slate-600 leading-relaxed mt-5">{t.school_p}</p>
          </Reveal>
        </div>
      </section>

      {/* ---------- preguntas ---------- */}
      <section id="faq" className="bg-[#fff8f0] scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 py-16 lg:py-24">
          <Reveal>
            <h2 className="font-go text-3xl sm:text-4xl font-bold">{t.faq_h}</h2>
            <div className="mt-9 divide-y divide-orange-200/70 border-y border-orange-200/70">
              {t.faq.map(([q, a], i) => (
                <div key={q}>
                  <button onClick={() => setAbierta(abierta === i ? -1 : i)}
                          data-testid={`faq-accordion-item-${i + 1}`}
                          className="w-full flex items-center justify-between gap-4 text-left py-5 group">
                    <span className="font-go font-bold text-[16.5px] group-hover:text-[#ff731d] transition-colors">{q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform duration-300 ${abierta === i ? "rotate-180" : ""}`} />
                  </button>
                  <div className="overflow-hidden transition-all duration-300"
                       style={{ maxHeight: abierta === i ? 280 : 0, opacity: abierta === i ? 1 : 0 }}>
                    <p className="text-[15px] text-slate-600 leading-relaxed pb-5 pr-8">{a}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- cierre ---------- */}
      <section className="bg-[#fff8f0] pb-24 md:pb-16">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-[#0d1b2a] text-white px-7 py-14 lg:px-16 lg:py-16 text-center"
                 data-testid="final-cta">
              <div className="absolute -left-20 -bottom-24 h-[320px] w-[320px] rounded-full bg-[#ff731d]/25 blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-center gap-1 text-[#ff9d5c]">
                  {[0, 1, 2, 3, 4].map((n) => <Star key={n} className="h-4 w-4 fill-current" />)}
                </div>
                <h2 className="font-go text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{t.final_h}</h2>
                <p className="text-slate-300 mt-3 text-lg">{t.final_sub}</p>
                <div className="mt-8 flex justify-center">
                  <Cta testid="final-cta-btn" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="bg-[#0d1b2a] text-center py-10 text-[12.5px] text-slate-400">
        <img src="/wssgo-logo-white.png" alt="WSS GO — by We Speak Spanish" loading="lazy"
             className="h-12 sm:h-14 w-auto mx-auto mb-7" data-testid="footer-logo-img" />
        <a href="https://wespeakspanish-cr.com"
           className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-white border border-white/25 hover:border-[#ff731d] hover:text-[#ff9d5c] rounded-full px-5 py-2.5 transition-colors"
           data-testid="footer-back-site">
          <ArrowLeft className="h-4 w-4" /> {t.back_site}
        </a>
        <div className="mt-6">{t.foot}</div>
      </footer>

      {/* ---------- barra fija de móvil ---------- */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 backdrop-blur-md bg-white/95 border-t border-slate-200 px-4 py-3">
        <a href="#signup" data-testid="sticky-mobile-cta-btn"
           className="flex items-center justify-center gap-2 w-full bg-[#ff731d] text-white font-bold rounded-xl py-3.5 shadow-lg shadow-orange-500/25">
          {t.cta(dias)} <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}
